import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  inject,
  input,
  linkedSignal,
  resource,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

import { PORT_SUBSCRIPTIONS_OF_CUSTOMER_QUERY } from "../../../apis/networkdashboard/static/graphql-queries/subscription-list";
import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { StartLoadMessage } from "../../../helpers/self-service/models/types";
import { ModifyL2VPNAddPort } from "../../../helpers/self-service/modify_l2vpn";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { isValidVlan } from "../../../helpers/self-service/vlanhelper";
import { L2vpnSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { LoaderComponent } from "../../common/loader/loader";
import { SelfserviceToggle } from "../selfservice-toggle/selfservice-toggle";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";
import { ServicePort } from "./ServicePort";

@Component({
  selector: "selfservice-add-port",
  templateUrl: "selfservice-add-port.html",
  styleUrls: ["selfservice-add-port.scss", "../selfservice-loader/selfservice-loader.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    FormsModule,
    GuidPipe,
    LoaderComponent,
    SelfserviceToggle,
    SelfserviceWizard,
    TranslatePipe,
  ],
})
export class SelfserviceAddPort {
  private auth = inject(AuthService);
  private apollo = inject(Apollo);

  protected subscription = input.required<L2vpnSubscriptionDetails>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();

  protected esis = computed(() => this.subscription().esis);
  protected nrOfEsis = computed(() => this.esis().length);

  protected summaryItemOneExpanded = signal(true);
  protected summaryItemTwoExpanded = signal(false);

  private portsResource = resource({
    params: () => ({ customerId: this.auth.viewingCustomer().customerId, subscription: this.subscription() }),
    loader: async ({ params: { customerId, subscription } }) => {
      const result = await firstValueFrom(
        this.apollo.query<{ subscriptions: { page: Array<ServicePort> } }>({
          query: PORT_SUBSCRIPTIONS_OF_CUSTOMER_QUERY,
          fetchPolicy: "network-only",
          variables: { customer: customerId },
        }),
      );
      const portsInSubscription = new Set(
        subscription.esis.flatMap((esi) => esi.endpoints?.map((e) => e.port?.subscriptionId) ?? []),
      );
      return (result.data?.subscriptions.page ?? [])
        .filter((port) => this.isPortValid(portsInSubscription, port))
        .map((port) => ({ ...port, selected: false }) as ServicePort);
    },
  });

  protected loadingData = this.portsResource.isLoading;
  protected availablePorts = linkedSignal(() => this.portsResource.value() ?? []);
  protected selectedPorts = computed(() => this.availablePorts().filter((port) => port.selected));

  protected readonly stepCanContinue = (step: number) =>
    step !== 1 || (!this.loadingData() && this.selectedPorts().length > 0);

  protected togglePort(port: ServicePort) {
    this.availablePorts.update((ports) => ports.map((p) => (p === port ? { ...p, selected: !p.selected } : p)));
  }

  protected toggleSummaryItemOne() {
    this.summaryItemOneExpanded.update((value) => !value);
  }

  protected toggleSummaryItemTwo() {
    this.summaryItemTwoExpanded.update((value) => !value);
  }

  private isPortValid(portsInSubscription: Set<string | undefined>, port: ServicePort) {
    return (
      !portsInSubscription.has(port.subscriptionId)
      && port.portMode === "tagged"
      && !port.tags.includes("MSC")
      && !port.services.some((s) => s.product.type === "IP" || s.product.type === "Firewall")
    );
  }

  protected validVlan(port: ServicePort, vlanString: string) {
    return isValidVlan(port, vlanString);
  }

  protected readonly buildCommand = () => {
    const action = SelfServiceCommandKeys.ModifyL2VpnAddPort;
    const modify = new ModifyL2VPNAddPort();
    modify.subscription = this.subscription();
    modify.setEsis(this.selectedPorts().map((port) => [{ subscription_id: port.subscriptionId, vlan: port.vlan }]));
    const command = new SelfServiceCommand(this.subscription().subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };
}
